import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Clock, CheckCircle, FileCheck, Award, ArrowRight, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import InquiryForm from '../components/InquiryForm';
import SEO, { breadcrumbSchema, faqSchema } from '../components/SEO';
import { visaPagesData } from '../data/detailPages';

const VisaDetail = () => {
  const { slug } = useParams();
  const data = visaPagesData[slug];

  if (!data) return <Navigate to="/visa-services" replace />;

  const schemas = [
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Visa Services', path: '/visa-services' },
      { name: `${data.country} Visa`, path: `/visa/${slug}` }
    ]),
    faqSchema(data.faqs)
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title={data.seoTitle}
        description={data.seoDescription}
        keywords={data.seoKeywords}
        path={`/visa/${slug}`}
        image={data.heroImage}
        schema={schemas}
      />

      {/* Hero */}
      <section
        className="relative min-h-[420px] flex items-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.55), rgba(15, 23, 42, 0.75)), url('${data.heroImage}')`
        }}
      >
        <div className="container mx-auto px-4 py-16 text-white">
          <nav className="text-sm text-white/80 mb-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/visa-services" className="hover:text-white">Visa Services</Link>
            <span className="mx-2">/</span>
            <span>{data.country} Visa</span>
          </nav>
          <div className="flex items-center gap-3 mb-4">
            <span className="text-5xl">{data.flag}</span>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight">{data.title}</h1>
          </div>
          <p className="text-lg text-white/90 max-w-3xl mb-6">{data.overview}</p>
          <div className="flex flex-wrap gap-4">
            <a href="https://wa.me/919016930091" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[#25D366] hover:bg-[#1fb855]">WhatsApp Now</Button>
            </a>
            <a href="tel:+919016930091">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900">
                <Phone size={18} className="mr-2" /> +91 90169 30091
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Quick facts strip */}
      <section className="bg-gradient-to-r from-[#2D8DB4] to-[#F7945D] text-white">
        <div className="container mx-auto px-4 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Starting Price', value: data.price, icon: Award },
            { label: 'Processing', value: data.processingTime, icon: Clock },
            { label: 'Validity', value: data.validity, icon: CheckCircle },
            { label: 'Entry Type', value: data.entryType, icon: FileCheck }
          ].map((it, i) => {
            const Icon = it.icon;
            return (
              <div key={i} className="flex items-center gap-3">
                <Icon size={28} />
                <div>
                  <div className="text-xs uppercase tracking-wider text-white/80">{it.label}</div>
                  <div className="font-bold">{it.value}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Main content + sticky inquiry */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-12">
              {/* Requirements */}
              <div>
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Documents Required</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.requirements.map((r, i) => (
                    <div key={i} className="flex items-start gap-2 bg-gray-50 p-3 rounded-lg">
                      <CheckCircle size={18} className="text-[#2D8DB4] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{r}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Visa Types & Pricing */}
              <div>
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Visa Options & Pricing</h2>
                <div className="space-y-3">
                  {data.visaTypes.map((vt, i) => (
                    <Card key={i} className="border-l-4 border-l-[#F7945D]">
                      <CardContent className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <div>
                          <h4 className="font-bold text-gray-900">{vt.name}</h4>
                          <p className="text-sm text-gray-500">Stay duration: {vt.stay}</p>
                        </div>
                        <div className="text-2xl font-bold text-[#F7945D]">{vt.price}</div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3">* Prices include our service fee. Government visa fees, courier and biometric charges may apply additionally based on destination.</p>
              </div>

              {/* Process */}
              <div>
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Our 4-Step Process</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {['Free Consultation', 'Document Review', 'Application Submit', 'Visa Delivered'].map((step, i) => (
                    <div key={i} className="text-center">
                      <div className="w-14 h-14 bg-gradient-to-br from-[#2D8DB4] to-[#F7945D] text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3">
                        {i + 1}
                      </div>
                      <h4 className="font-semibold text-sm">{step}</h4>
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQ */}
              <div>
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Frequently Asked Questions</h2>
                <Accordion type="single" collapsible className="w-full">
                  {data.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`item-${i}`}>
                      <AccordionTrigger className="text-left font-semibold">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>

            {/* Sticky Inquiry Form */}
            <aside className="lg:sticky lg:top-28 self-start">
              <InquiryForm
                inquiryType="visa"
                defaultSubject={`${data.country} Visa Inquiry`}
                defaultDetails={{ visa_country: data.country, page: `/visa/${slug}` }}
                title={`Apply for ${data.country} Visa`}
                subtitle="Free consultation — fill the form, we'll call you within 30 mins"
              />
            </aside>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-gradient-to-r from-[#2D8DB4] to-[#F7945D] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-3">Ready to apply for your {data.country} visa?</h2>
          <p className="mb-6">Talk to a Varni Visa expert today. Free consultation, no obligation.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/visa-services">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#2D8DB4]">
                See All Visas <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            <a href="https://wa.me/919016930091" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-white text-[#2D8DB4] hover:bg-gray-100">WhatsApp Now</Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VisaDetail;
